import api from '../api.jsx';
import isArray from 'lodash/isArray';
import {
    prepareEventIdentifiers,
    prepareConditionIdentifiers,
    getPreparedConditionObj,
    getPreparedEventObj,
    prepareConditionToSend,
    prepareEventToSend,
    prepareActionToSend,
    triggerTimeEventOptions,
    checkInOption,
} from '../components/trigger/trigger-edit/helpers/helpers.js';
import {
    HAS_PETS_STATUS,
    PROPERTIES_CHANNELS_SELECT_TYPE,
    RECURRING_IDENTIFIER,
    DATE_IDENTIFIER,
    HAS_NO_PETS_STATUS,
} from '../components/trigger/trigger-edit/helpers/constants.js';
import { TRIGGER_METADATA, TRIGGER_ENDPOINT } from '../endpoints.js';

export default {
    namespaced: true,
    state() {
        return {
            allowed_actions: ['EMAIL', 'PUSH'],
            apply_for_each_property: false,
            apply_once_per_reservation: true,
            apply_to_last_minute_reservations: true,
            actions: [],
            conditions: [[]],
            condition_identifiers: [],
            condition_options: [],
            channels: [],
            channels_options: [],
            channels_select_type: 2,
            creator: null,
            is_enabled: false,
            is_limited: false,
            editing: false,
            events: [],
            event_options: [],
            event_identifiers: [],
            email_responses: [],
            unlayer_email_responses: [],
            properties: [],
            properties_options: [],
            properties_select_type: 2,
            properties_select_type_options: PROPERTIES_CHANNELS_SELECT_TYPE,
            readonly: false,
            report_templates: [],
            report_settings_options: [],
            send_to_options: {
                static: [],
                dynamic: [],
            },
            send_to: [],
            selected_trigger_for_copy: null,
            tags_length_obj: {},
            team_members: [],
            team_members_options: [],
            title: '',
            trigger_options: [],
            uploaded_files: [],
            type: '',
        };
    },
    getters: {
        actions: (state) => state.actions,
        applyForEachProperty: (state) => state.apply_for_each_property,
        applyOncePerReservation: (state) => state.apply_once_per_reservation,
        applyToLastMinuteReservations: (state) => state.apply_to_last_minute_reservations,
        conditions: (state) => state.conditions,
        conditionIdentifiers: (state) => state.condition_identifiers,
        conditionOptions: (state) => state.condition_options,
        channels: (state) => state.channels,
        channelsOptions: (state) => state.channels_options,
        channelsSelectType: (state) => state.channels_select_type,
        editing: (state) => state.editing,
        events: (state) => state.events,
        eventOptions: (state) => state.event_options,
        eventIdentifiers: (state) => state.event_identifiers,
        emailResponsesOptions: (state) => state.email_responses,
        emailUnlayerResponsesOptions: (state) => state.unlayer_email_responses,
        isEnabled: (state) => state.is_enabled,
        isLimited: (state) => state.is_limited,
        properties: (state) => state.properties,
        propertiesSelectType: (state) => state.properties_select_type,
        propertiesSelectTypeOptions: (state) => state.properties_select_type_options,
        propertiesOptions: (state) => state.properties_options,
        reportTemplatesOptions: (state) => state.report_templates,
        unlayerPdfTemplatesOptions: (state) => state.unlayer_pdf_templates,
        reportSettingsOptions: (state) => state.report_settings_options,
        sendTo: (state) => state.send_to,
        sendToOptions: (state) => state.send_to_options,
        selectedTriggerForCopy: (state) => state.selected_trigger_for_copy,
        tagsLengthObj: (state) => state.tags_length_obj,
        teamMembers: (state) => state.team_members,
        teamMembersOptions: (state) => state.team_members_options,
        title: (state) => state.title,
        triggerOptions: (state) =>
            state.trigger_options.map((item) => ({
                value: item.id,
                text: item.title,
            })),
        uploadedFilesOptions: (state) => state.uploaded_files,
        hasActions: (state) => state.actions.length > 0,
        hasEvents: (state) => state.events.length > 0,
        hasRecurringOrDateEvent: (state) => {
            return state.events.some((item) => item.source === DATE_IDENTIFIER || item.source === RECURRING_IDENTIFIER);
        },
        allowedActions: (state) => state.allowed_actions,
        readonly: (state) => state.readonly,
        creator: (state) => state.creator,
        type: (state) => state.type,
    },
    mutations: {
        setActions(state, value) {
            state.actions = value;
        },
        setAllowedActions(state, value) {
            state.allowed_actions = value;
        },
        setConditions(state, conditions) {
            state.conditions = conditions;
        },
        setConditionOptions(state, options) {
            state.condition_options = options;
        },
        setConditionIdentifiers(state, identifiers) {
            state.condition_identifiers = identifiers;
        },
        setChannels(state, value) {
            state.channels = value;
        },
        setChannelsOptions(state, value) {
            state.channels_options = value;
        },
        setChannelsSelectType(state, value) {
            if (value === 2 && state.channels.length > 0) {
                state.channels = [];
            }
            state.channels_select_type = value;
        },
        setCreator(state, value) {
            state.creator = value;
        },
        setEditing(state, value) {
            state.editing = value;
        },
        setEnabled(state, value) {
            state.is_enabled = value;
        },
        setEvents(state, events) {
            state.events = events;
        },
        setEventOptions(state, value) {
            state.event_options = value;
        },
        setEventIdentifiers(state, value) {
            state.event_identifiers = value;
        },
        setEmailResponses(state, value) {
            state.email_responses = value;
        },
        setUnlayerEmailResponses(state, value) {
            state.unlayer_email_responses = value;
        },
        setUnlayerPdfTemplates(state, value) {
            state.unlayer_pdf_templates = value;
        },
        setForEachProperty(state, value) {
            state.apply_for_each_property = value;
        },
        setIsLimited(state, value) {
            state.is_limited = value;
        },
        setOncePerRes(state, value) {
            state.apply_once_per_reservation = value;
        },
        setLastMinute(state, value) {
            state.apply_to_last_minute_reservations = value;
        },
        setProperties(state, value) {
            state.properties = value;
        },
        setPropertiesSelectType(state, value) {
            if (value === 2 && state.properties.length > 0) {
                state.properties = [];
            }
            state.properties_select_type = value;
        },
        setPropertiesOptions(state, value) {
            state.properties_options = value;
        },
        setReadonly(state, value) {
            state.readonly = value;
        },
        setReportTemplates(state, value) {
            state.report_templates = value;
        },
        setTagsLengthObj(state, value) {
            state.tags_length_obj = value;
        },
        setTeamMembers(state, value) {
            state.team_members = value;
        },
        setTeamMembersOptions(state, value) {
            state.team_members_options = value;
        },
        setTitle(state, title) {
            state.title = title;
        },
        setTriggerOptions(state, value) {
            state.trigger_options = value;
        },
        setSendTo(state, value) {
            state.send_to = value;
        },
        setSendToOptions(state, value) {
            state.send_to_options = value;
        },
        setUploadedFiles(state, value) {
            state.uploaded_files = value;
        },
        setReportSettingsOptions(state, value) {
            state.report_settings_options = value;
        },
        setType(state, value) {
            state.type = value;
        },
    },
    actions: {
        addAction({ commit, state }) {
            commit('setActions', [...state.actions, { type: '' }]);
        },
        addCondition({ commit, state }, index) {
            const conditions = JSON.parse(JSON.stringify(state.conditions));
            if (index !== null) {
                conditions[index].push({
                    source: '',
                    operator: '',
                    target: '',
                });
            } else {
                conditions.push({
                    source: '',
                    operator: '',
                    target: '',
                });
            }
            commit('setConditions', conditions);
            commit('setEditing', true);
        },
        addEvent({ commit, state }) {
            commit('setEvents', [...state.events, { source: '', operator: '', target: '' }]);
            commit('setEditing', true);
        },
        async copyTrigger({ dispatch }, { triggerId }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT(triggerId)}clone/`);
            const actions = data.actions.filter((item) => !!item).map((item) => ({
                ...item,
                id: null,
            }));
            data.actions = actions;

            dispatch('setTriggerData', { data, isCopy: true });
            return data;
        },
        async fetchTriggersOptions({ commit }) {
            const { data } = await api.get(TRIGGER_ENDPOINT());
            commit('setTriggerOptions', data);
        },
        async fetchMetadata({ commit }) {
            const { data } = await api.get(TRIGGER_METADATA());
            const eventOptions = data.events_options.options;
            const eventIdentifiers = prepareEventIdentifiers(data.events_options.identifiers, data.event_types);
            eventOptions.ARRIVAL_DATE_PAST = triggerTimeEventOptions;
            eventOptions.ARRIVAL_DATE_FUTURE = [checkInOption, ...triggerTimeEventOptions];
            eventOptions.OWNER_BLOCK = eventOptions.OWNER_BLOCK.map((item) => ({
                ...item,
                text: item.text + 'd',
            }));
            eventOptions.DEPARTURE_DATE_PAST = triggerTimeEventOptions;
            eventOptions.DEPARTURE_DATE_FUTURE = triggerTimeEventOptions;
            eventOptions.RECURRING = [];
            eventOptions.DATE = [];

            const conditionOptions = data.conditions_options.options;

            conditionOptions.PETS = [
                {
                    text: 'Have',
                    value: HAS_PETS_STATUS,
                },
                {
                    text: 'Not Have',
                    value: HAS_NO_PETS_STATUS,
                },
            ];
            const conditionIdentifiers = prepareConditionIdentifiers(
                data.conditions_options.identifiers,
                data.condition_types
            );
            const channelsOptions = data.trigger_channels;
            const sendToOptions = data.trigger_receivers;
            commit('setEventOptions', eventOptions);
            commit('setEventIdentifiers', eventIdentifiers);
            commit('setConditionOptions', conditionOptions);
            commit('setConditionIdentifiers', conditionIdentifiers);
            commit('setChannelsOptions', channelsOptions);
            commit('setSendToOptions', sendToOptions);
        },
        clearTriggerData({ dispatch }, hasCustomSMSGateway) {
            let allowed_actions = ['EMAIL', 'PUSH'];
            if (hasCustomSMSGateway) {
                allowed_actions.push('SMS');
            }
            const data = {
                apply_for_each_property: false,
                apply_once_per_reservation: true,
                apply_to_last_minute_reservations: true,
                actions: [],
                allowed_actions: allowed_actions,
                conditions_store: { conditions: [[]] },
                channels_list: [],
                channels_select_type: 2,
                creator: null,
                is_enabled: false,
                events_store: { events: [] },
                properties: [],
                properties_select_type: 2,
                readonly: false,
                title: '',
                trigger_receivers: [],
                type: '',
            };
            dispatch('setTriggerData', { data, isCopy: false });
        },
        async fetchProperties({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}properties/`);
            const propertiesOptions = data.map((item) => {
                return {
                    value: item.property_id,
                    text: item.name,
                };
            });
            commit('setPropertiesOptions', propertiesOptions);
        },
        async fetchReportTemplates({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}report-templates/`);
            const report_templates = [
                { value: null, text: '-Attach PDF Template-' },
                ...data.map((item) => {
                    return {
                        value: item.id,
                        text: item.report_name,
                    };
                }),
            ];
            commit('setReportTemplates', report_templates);
        },
        async fetchReportSettings({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}report-settings/`);
            const report_settings_options = [
                ...data.map((item) => {
                    return {
                        value: item.id,
                        text: item.name,
                    };
                }),
            ];
            commit('setReportSettingsOptions', report_settings_options);
        },
        async fetchEmailResponses({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}email-responses/`);
            const email_responses = data.map((item) => {
                return {
                    value: item.id,
                    text: item.title,
                };
            });
            commit('setEmailResponses', email_responses);
        },
        async fetchUnlayerEmailResponses({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}unlayer-email-responses/`);
            const unlayer_email_responses = data.map((item) => {
                return {
                    value: item.id,
                    text: item.name,
                };
            });
            commit('setUnlayerEmailResponses', unlayer_email_responses);
        },
        async fetchUnlayerPdfTemplates({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}unlayer-pdf-templates/`);
            const unlayer_pdf_templates = [
                { value: null, text: '-Attach PDF Report-' },
                ...data.map((item) => {
                    return {
                        value: item.id,
                        text: item.name,
                    };
                }),
            ];
            commit('setUnlayerPdfTemplates', unlayer_pdf_templates);
        },
        async fetchUploadedFiles({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}uploaded-files/`);
            const uploaded_files = [
                { value: null, text: '-Attach Uploaded File-' },
                { text: 'Document (Chosen By Invoice)', value: -1 },
                ...data.map((item) => {
                    return {
                        value: item.id,
                        text: item.description,
                    };
                }),
            ];
            commit('setUploadedFiles', uploaded_files);
        },
        async fetchTeamMembers({ commit }) {
            const { data } = await api.get(`${TRIGGER_ENDPOINT()}team-members/`);
            const teamMembersOptions = data.map((item) => {
                return {
                    value: item.id,
                    text: `${item.first_name} ${item.last_name}`,
                };
            });
            commit('setTeamMembersOptions', teamMembersOptions);
        },
        setTriggerData({ commit }, { data, isCopy }) {
            data.properties_select_type = data.properties.length === 0 ? 2 : data.properties_select_type;
            data.channels_select_type = data.channels_list.length === 0 ? 2 : data.channels_select_type;
            commit('setPropertiesSelectType', data.properties_select_type);
            commit('setChannelsSelectType', data.channels_select_type);
            commit('setTitle', data.title);
            commit('setProperties', data.properties);
            commit('setEnabled', data.is_enabled);
            commit('setChannels', data.channels_list);
            commit('setLastMinute', data.apply_to_last_minute_reservations);
            commit('setOncePerRes', data.apply_once_per_reservation);
            commit('setForEachProperty', data.apply_for_each_property);
            commit('setIsLimited', data.is_limited);
            commit('setAllowedActions', data.allowed_actions);
            commit('setReadonly', data.is_limited);
            commit('setCreator', data.creator);
            commit('setType', data.type);

            const actions = data.actions.filter((item) => !!item).map((item) => {
                if (item.type === 'SMS') {
                    return {
                        ...item,
                        sms: item.message,
                    };
                }
                return {
                    ...item,
                    type: item.type,
                    email: item.message,
                    subject: item.subject,
                    recipients: item.email_receivers,
                    selectedCalendarRange: item.calendar_range,
                    selectedEmailResponse: '',
                    selectedReportTemplate: item.report_template,
                    selectedReportSettings: item.report_settings,
                    selectedUploadedFile: item.property_file ? -1 : item.uploaded_file,
                    selectedUnlayerPdfTemplate: item.unlayer_template,
                    tagSearch: '',
                    selectedTag: '',
                    htmlTagsOptions: [],
                };
            });
            commit('setActions', actions);

            const sendTo = new Set(data.trigger_receivers.map((item) => item.type));

            const teamMembers = data.trigger_receivers.map((item) => item.team_members).flat();
            if (sendTo.has(1)) {
                teamMembers.unshift('SUBSCRIBER');
                sendTo.add(2);
            }
            commit('setSendTo', [...sendTo]);
            commit('setTeamMembers', teamMembers);

            const events = data.events_store.events.map((item) => {
                return getPreparedEventObj(item, isCopy);
            });
            commit('setEvents', events);
            const conditions = data.conditions_store.conditions.map((item) => {
                if (isArray(item)) {
                    return item.map((i) => {
                        return getPreparedConditionObj(i);
                    });
                } else {
                    return getPreparedConditionObj(item);
                }
            });
            const defaultConditions = [[]];
            const hasOrConditions = conditions.length > 0 && conditions.some((item) => Array.isArray(item));
            const triggerConditions = hasOrConditions ? conditions : [[], ...conditions];
            commit('setConditions', conditions.length === 0 ? defaultConditions : triggerConditions);
        },
        async fetchTriggerData({ dispatch }, { id }) {
            if (id) {
                const { data } = await api.get(`${TRIGGER_ENDPOINT(id)}`);
                dispatch('setTriggerData', { data, isCopy: false });
            }
        },
        removeAction({ commit, state }, index) {
            const actions = JSON.parse(JSON.stringify(state.actions));
            actions.splice(index, 1);
            commit('setActions', actions);
            commit('setEditing', true);
        },
        removeEvent({ commit, state }, { index }) {
            const events = JSON.parse(JSON.stringify(state.events));
            events.splice(index, 1);
            commit('setEvents', events);
            commit('setEditing', true);
        },
        removeCondition({ commit, state }, { index, nestedIndex }) {
            const conditions = JSON.parse(JSON.stringify(state.conditions));
            if (nestedIndex !== null) {
                conditions[index].splice(nestedIndex, 1);
            } else {
                conditions.splice(index, 1);
            }
            commit('setConditions', conditions);
            commit('setEditing', true);
        },
        async save({ getters, dispatch, commit }, { id }) {
            const buildPayload = () => {
                const hasRecurringOrDateEvent = getters.events.some(
                    (item) => item.source === DATE_IDENTIFIER || item.source === RECURRING_IDENTIFIER
                );
                const preparedConditions = hasRecurringOrDateEvent
                    ? []
                    : getters.conditions
                          .map((item) => {
                              if (isArray(item)) {
                                  return item.filter((i) => i.source).map((i) => prepareConditionToSend(i));
                              }
                              return prepareConditionToSend(item);
                          })
                          .filter((item) => {
                              if (isArray(item)) {
                                  return item.length > 0;
                              }
                              return !!item.identifier;
                          });
                const preparedEvents = getters.events
                    .map((item) => {
                        return prepareEventToSend(item);
                    })
                    .filter((item) => !!item.identifier);
                const preparedEmailActions = getters.actions.filter((item) => item.type === 'EMAIL').map((item) => {
                    return prepareActionToSend(item);
                });
                const smsAction = getters.actions.find((item) => item.type === 'SMS');
                const preparedSmsAction = smsAction ? prepareActionToSend(smsAction) : null;
                const preparedPushNotificationActions = getters.actions
                    .filter((item) => item.type === 'PUSH')
                    .map((item) => {
                        return prepareActionToSend(item);
                    });
                const sendTo = getters.sendTo;
                if (getters.teamMembers.includes('SUBSCRIBER') && !getters.sendTo.includes(1)) {
                    sendTo.push(1);
                }
                const payload = {
                    apply_for_each_property: getters.applyForEachProperty,
                    apply_to_last_minute_reservations: getters.applyToLastMinuteReservations,
                    apply_once_per_reservation: getters.applyOncePerReservation,
                    properties: getters.properties,
                    channels: getters.channels.join(','),
                    channels_select_type: getters.channelsSelectType !== 0 ? 1 : getters.channelsSelectType,
                    properties_select_type: getters.propertiesSelectType !== 0 ? 1 : getters.propertiesSelectType,
                    title: getters.title,
                    is_enabled: getters.isEnabled,
                    send_to: {
                        receiver_type: sendTo,
                        team_members: getters.teamMembers.filter((item) => item !== 'SUBSCRIBER'),
                    },
                    conditions_store: {
                        conditions: preparedConditions,
                    },
                    events_store: {
                        events: preparedEvents,
                    },
                    email_actions: preparedEmailActions,
                    sms_action: preparedSmsAction,
                    push_notification_actions: preparedPushNotificationActions,
                };
                return payload;
            };
            if (id) {
                const { data } = await api.patch(TRIGGER_ENDPOINT(id), buildPayload());
                dispatch('setTriggerData', { data, isCopy: false });
                commit('setEditing', false);
                return data;
            } else {
                const { data } = await api.post(TRIGGER_ENDPOINT(), buildPayload());
                dispatch('setTriggerData', { data, isCopy: false });
                commit('setEditing', false);
                return data;
            }
        },
    },
};
